import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewAs, setViewAs] = useState(() => {
    const id = localStorage.getItem("tmic_view_as");
    const name = localStorage.getItem("tmic_view_as_name");
    return id ? { id, name } : null;
  });

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/me");
      setProfile(data);
      setPermissions(data.permissions || []);
      const orgs = data.organisations || [];
      setOrganisations(orgs);
      setIsPlatformAdmin(!!data.is_platform_admin);
      // Resolve active org: stored choice if still a member, else the one the backend picked.
      const stored = localStorage.getItem("tmic_org");
      const active = orgs.find((o) => o.id === stored) ? stored : (data.org_id || orgs[0]?.id || null);
      if (active) localStorage.setItem("tmic_org", active);
      setCurrentOrg(orgs.find((o) => o.id === active) || (data.org_id ? { id: data.org_id } : null));
    } catch (e) {
      setProfile(null);
      setPermissions([]);
      setOrganisations([]);
      setIsPlatformAdmin(false);
    }
  }, []);

  const switchOrg = useCallback(async (orgId) => {
    localStorage.setItem("tmic_org", orgId);
    // The reload re-bootstraps via INITIAL_SESSION -> loadProfile, so no extra fetch here.
    window.location.reload();
  }, []);

  const enterViewAs = useCallback(async (person) => {
    const name = `${person.first_name} ${person.last_name}`;
    localStorage.setItem("tmic_view_as", person.id);
    localStorage.setItem("tmic_view_as_name", name);
    setViewAs({ id: person.id, name });
    await loadProfile();
  }, [loadProfile]);

  const exitViewAs = useCallback(async () => {
    localStorage.removeItem("tmic_view_as");
    localStorage.removeItem("tmic_view_as_name");
    setViewAs(null);
    await loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    let done = false;
    const finish = () => { if (!done) { done = true; setLoading(false); } };
    // Safety net: never let the app hang on the loading gate.
    const timer = setTimeout(finish, 6000);

    // Supabase fires INITIAL_SESSION immediately on subscribe (with the current session, or
    // null when signed out). This single listener is the ONE authoritative place we resolve
    // the session and load the profile — there is no separate getSession()+loadProfile()
    // pass, which is what previously caused /me to be requested twice on every load.
    const { data: sub } = supabase.auth.onAuthStateChange((event, sess) => {
      setSession(sess);
      if (sess) {
        // Load the profile here for the restored session on page load (INITIAL_SESSION) and
        // when the user record changes (USER_UPDATED). We deliberately do NOT load on SIGNED_IN
        // — login() does that itself and awaits it — nor on the silent TOKEN_REFRESHED. This is
        // what removes the second /me that used to fire on every load.
        if (event === "INITIAL_SESSION" || event === "USER_UPDATED") {
          loadProfile().finally(finish);
        } else {
          finish();
        }
      } else {
        setProfile(null);
        setPermissions([]);
        setOrganisations([]);
        setIsPlatformAdmin(false);
        finish();
      }
    });
    return () => { clearTimeout(timer); sub.subscription.unsubscribe(); };
  }, [loadProfile]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    setSession(data.session);
    // Load the profile once here (the SIGNED_IN listener intentionally skips it) so the
    // forced-password-change / permission gates are ready the moment we navigate.
    await loadProfile();
    return data.session;
  };

  const logout = async () => {
    localStorage.removeItem("tmic_view_as");
    localStorage.removeItem("tmic_view_as_name");
    localStorage.removeItem("tmic_org");
    setViewAs(null);
    await supabase.auth.signOut();
    setProfile(null);
    setPermissions([]);
  };

  const hasPerm = (...codes) => {
    if (permissions.includes("administrator")) return true;
    return codes.some((c) => permissions.includes(c));
  };
  const isSuperAdmin = permissions.includes("super_admin");

  return (
    <AuthContext.Provider value={{ session, profile, permissions, loading, login, logout, hasPerm, isSuperAdmin, reloadProfile: loadProfile, viewAs, enterViewAs, exitViewAs, organisations, currentOrg, isPlatformAdmin, switchOrg }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
